<template>
  <base-draggable-modal
      :name="modal_name"
      max-height="80vh"
      width="50vw"
      :fullscreen="$vuetify.display.xsOnly"
      :ref="modal_name">
    <v-card rounded="xl">
      <base-modal>
        <template #header>
          <v-toolbar class="draggable_selector" flat color="secondary" density="compact">
            <v-toolbar-title v-if="label" class="text-primary">{{ label }}</v-toolbar-title>
            <v-toolbar-title v-else class="text-primary">{{ $store.getters.translate('create') }} {{ $store.getters.translate(page.model) }}</v-toolbar-title>
          </v-toolbar>
        </template>
        <template #content>
            <v-card-text>
              <!--USUAL FIELDS-->
              <template v-for="field in usual_fields">
                <!--ASSET NAME FIELD-->
                <v-combobox
                    v-if="page.name === 'assets' && record.parent_id && field.name === 'name'"
                    density="default"
                    class="reg-normal text-primary mt-3"
                    base-color="border-tertiary"
                    bg-color="secondary"
                    color="primary"
                    v-model="record.name"
                    :items="children_assets"
                    :return-object="false"
                    item-title="name"
                    item-value="name"
                    hide-details
                    clearable
                    @update:model-value="updateDescription">
                  <template #label>
                    <span class="sm-normal text-subtle">{{ $store.getters.translate('name') }}<strong>*</strong></span>
                  </template>
                </v-combobox>
                <!--FIELDSET OPTIONS FIELD-->
                <v-combobox
                    v-else-if="page.name === 'fieldsets' && field.name === 'alternates'"
                    density="default"
                    class="reg-normal text-primary mt-3"
                    base-color="border-tertiary"
                    bg-color="secondary"
                    color="primary"
                    v-model="record.alternates"
                    chips
                    closable-chips
                    hide-details
                    clearable
                    multiple>
                  <template #label>
                    <span class="sm-normal text-subtle">{{ $store.getters.translate('alternates') }}</span>
                  </template>
                </v-combobox>
                <!--FIELDSET OPTIONS FIELD-->
                <v-combobox
                    v-else-if="page.name === 'fieldsets' && record.type === 'enum' && record.options_source === 'custom_options' && field.name === 'options'"
                    density="default"
                    class="reg-normal text-primary mt-3"
                    base-color="border-tertiary"
                    bg-color="secondary"
                    color="primary"
                    v-model="record.options"
                    chips
                    closable-chips
                    hide-details
                    clearable
                    multiple>
                  <template #label>
                    <span class="sm-normal text-subtle">{{ $store.getters.translate('options') }}<strong>*</strong></span>
                  </template>
                </v-combobox>
                <!--PROJECT FIELD-->
                <v-autocomplete
                    v-else-if="field.name === 'project_id'"
                    density="default"
                    class="reg-normal text-primary mt-3"
                    base-color="border-tertiary"
                    bg-color="secondary"
                    color="primary"
                    v-model="record.project_id"
                    :items="field.available_options.filter(item => item.filter === false)"
                    :return-object="false"
                    item-value="value"
                    item-title="text"
                    hide-details
                    clearable>
                  <template #label>
                    <span class="sm-normal text-subtle">{{ $store.getters.translate('project_id') }}<strong>*</strong></span>
                  </template>
                </v-autocomplete>
                <!--TAG NUMBER FIELD-->
                <v-autocomplete
                    v-else-if="field.name === 'tag_number_id'"
                    density="default"
                    class="reg-normal text-primary mt-3"
                    base-color="border-tertiary"
                    bg-color="secondary"
                    color="primary"
                    v-model="record.tag_number_id"
                    :items="field.available_options.filter(item => item.filter === record.root_asset_id)"
                    :return-object="false"
                    item-value="value"
                    item-title="text"
                    hide-details
                    clearable>
                  <template #label>
                    <span class="sm-normal text-subtle">{{ $store.getters.translate('tag_number_id') }}<strong>*</strong></span>
                  </template>
                  <template v-if="canCreateNewModel(field.name)" #append>
                    <v-icon @click.stop="createNewModel(field.name)" color="primary" size="large" icon="mdi-plus"/>
                  </template>
                </v-autocomplete>
                <base-field
                    v-else-if="customModelConditions(field.name)"
                    :key="field.name"
                    :isDisabled="page.model === 'accesslevel' && field.name === 'level'"
                    :modelValue="record[field.name]"
                    :field="field"
                    :create="canCreateNewModel(field.name)"
                    @change="usualFieldDataChanged"
                    @create="createNewModel"/>
              </template>
              <!--PROJECT MANAGEMENT FIELDS-->
              <template v-if="page.name === 'projects'">
                <template v-for="field in project_management_fields">
                  <base-field
                      v-if="field.name !== 'name' && field.name !== 'project_id' &&
                      ((field.name !== 'construction_responsible_id' && field.name !== 'construction_responsible_text') || !record.project_management.construction_involved) &&
                      ((field.name !== 'commissioning_responsible_id' && field.name !== 'commissioning_responsible_text') || !record.project_management.commissioning_involved)"
                      :key="field.name"
                      :modelValue="record.project_management[field.name]"
                      :field="field"
                      @change="projectManagementFieldDataChanged"/>
                </template>
              </template>
              <!--CUSTOM FIELDS-->
              <base-field
                  v-if="record.custom_fields"
                  v-for="field in custom_fields"
                  :key="field.name"
                  :modelValue="record.custom_fields[field.name]"
                  :field="field"
                  @change="customFieldDataChanged"/>
              <!--UPLOAD FILES-->
              <v-row v-if="files_required" class="mt-3">
                <v-col v-cloak @drop.prevent="addDropFile" @dragover.prevent>
                  <v-file-input
                      v-model="new_files"
                      :multiple="multiple_files"
                      :label="$store.getters.translate('files')"
                      placeholder="Drop file to upload"
                      color="primary"
                      bg-color="white"
                      density="compact"
                      hide-details
                      show-size/>
                </v-col>
              </v-row>
              <v-row v-if="addresses_required" class="mt-0">
                <v-col>
                  <base-addresses v-model="record.addresses" @update="addressesChanged"/>
                </v-col>
              </v-row>
              <!--LINK ASSETS-->
              <v-row v-if="link_assets_required" class="mt-5">
                <v-col>
                  <base-list-assets
                      :page="page"
                      :root_asset_id="root_asset_id"
                      :parent_tags="record.tags"
                      :label="$store.getters.translate('linked_assets')"
                      :assets="record.assets"
                      :asset_ids="record.asset_ids"
                      :deletable="false"
                      :create="!link_assets_disabled"
                      :link_immediately="false"
                      @refresh="load"
                      @assetsAdded="assetsAdded"/>
                </v-col>
              </v-row>
              <!--TAGS-->
              <base-tags v-if="tags_required" :isDisabled="tags_disabled" :available_tags="available_tags" :modelValue="record.tags" :modal_name="page.name" @change="tagsChanged"/>
            </v-card-text>
        </template>
        <template #footer>
          <v-card-text>
            <v-row>
              <v-col>
                <base-action-button :width="'100%'" @action="reset">
                  <template #prepend><PhX style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("cancel") }}
                </base-action-button>
              </v-col>
              <v-col>
                <base-action-button :background="true" :width="'100%'" @action="save(true)">
                  <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("save") }}
                </base-action-button>
              </v-col>
              <v-col v-if="page.name === 'assets'">
                <base-action-button :background="true" :width="'100%'" @action="save(false)">
                  <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("save_and_create_more") }}
                </base-action-button>
              </v-col>
            </v-row>
          </v-card-text>
        </template>
      </base-modal>
    </v-card>
  </base-draggable-modal>
</template>

<script>
import {useAbility} from "@casl/vue";
import helpFunctions from "../../plugins/helpFunctions";
import BaseModal from "../../components/commonComponents/BaseModal";
import BaseDraggableModal from "../../components/commonComponents/BaseDraggableModal";
import BaseTags from "./BaseTags";
import BaseAddresses from "./BaseAddresses";
import BaseActionButton from "./BaseActionButton";
import BaseListAssets from "./BaseListAssets";
import {PhFloppyDisk, PhX} from "@phosphor-icons/vue";

export default {
  props: ["modal_name", "page", "label", "addresses_required", "files_required", "multiple_files", "link_assets_required", "link_assets_disabled", "available_tags", "tags_disabled"],
  components: {
    BaseModal,
    BaseDraggableModal,
    BaseTags,
    BaseAddresses,
    BaseActionButton,
    BaseListAssets,
    PhFloppyDisk, PhX,
  },
  setup() {
    const { can } = new useAbility();
    return { can }
  },
  data() {
    return {
      record: {},
      usual_fields: [],
      custom_fields: [],
      tags_required: false,
      //APPLICATION VARIABLES. NOT USED FOR OTHER MODELS
      application_equipment_group: null,
      application_equipment_category: null,
      application_explosive_atmosphere: null,
      //MARKERING VARIABLES. NOT USED FOR OTHER MODELS
      regulation: null,
      gas_group: null,
      protection_method: null,
      temperature_class: null,
      equipment_protection_level: null,
      //PROJECT VARIABLES. NOT USED FOR OTHER MODELS
      project_management_fields: [],
      platform_management: null,
      //PUNCHLIST ITEM VARIABLES. NOT USED FOR OTHER MODELS
      root_asset_id: null,
      employee_id: null,
      punchlist_owner_id: null,
      //ASSET VARIABLES. NOT USED FOR OTHER MODELS
      children_assets: [],
      //FILES VARIABLE
      new_files: []
    };
  },
  methods: {
    ...helpFunctions.modal_functions,
    async load() {
      this.cleanRecord();
      this.openModal(this.modal_name);
      await this.getFields();
      if(this.page.model === 'project') {
        await this.getProjectManagementFields();
      }
    },
    async save(reload) {
      if (helpFunctions.modelFieldsFilled(this.$toast, this.record, this.usual_fields)) {
        if(this.page.model === 'translation') {
          let new_key = this.$lodash.lowerCase(this.record.key);

          this.record.key = new_key.replaceAll('(', "_").replaceAll(')', "_").replaceAll('[', "_").replaceAll(']', "_")
              .replaceAll('-', "_").replaceAll('/', "_").replaceAll('\\', "_").replace(/ /g, "_");
        }
        if((this.page.model === 'projectmanagementanswer' || this.page.model === 'externallink') && this.record.url && !this.record.url.includes("://")) {
          this.$toast.error(this.$store.getters.translate("incorrect_website_format"));
        }
        else {
          let new_record = await helpFunctions.createModel(this.$toast, this.page.name, this.record);
          if(new_record) {
            if(this.multiple_files && this.new_files.length > 0) {
              await this.uploadFiles(this.new_files, new_record.id);
            }
            else if(typeof this.new_files === "object" && this.new_files.name) {
              await this.uploadOneFile(this.new_files, new_record.id);
            }
            if(this.page.model === 'translation') {
              await this.$store.dispatch("refreshTranslations");
            }
            if (this.page.model === 'projectmanagementanswer') {
              //CREATE EXTERNAL LINK IF ANSWER CONTAINS URL
              if (this.record.url) {
                await helpFunctions.createModel(this.$toast, helpFunctions.pages.externallinks.name, {
                  model_id: this.record.project_management_id,
                  model_type: 'projectmanagement',
                  name: this.record.name,
                  url: this.record.url,
                });
              }
            }
            this.$emit('refresh', new_record);
            if(reload) {
              this.reset();
            }
            else {
              this.record.id = null;
              this.record.name = "";
              this.record.description = "";
              await this.loadChildrenAssets();
            }
          }
        }
      }
    },
    reset() {
      this.cleanRecord();
      this.new_files = [];
      this.closeModal(this.modal_name);
    },
    cleanRecord() {
      if(this.page.model === 'company' || this.page.model === 'defect' || this.page.model === 'tagnumber') {
        this.record = {
          custom_fields: {},
        };
      }
      else if(this.page.model === 'project') {
        this.record = {
          project_management: {
            construction_involved: true,
            commissioning_involved: true
          },
          custom_fields: {},
        };
      }
      else if(this.page.model === 'projectmanagement') {
        this.record = {
          construction_involved: true,
          commissioning_involved: true
        };
      }
      else if(this.page.model === 'phase') {
        this.record = {
          shutdown_required: 'To be determined',
        };
      }
      else if(this.page.model === 'punchlistitem') {
        this.record = {
          assets: [],
          asset_ids: [],
        };
      }
      else if(this.page.model === 'article') {
        this.record = {
          assets: [],
          asset_ids: [],
          custom_fields: {},
        };
      }
      else if(this.page.model === 'document') {
        this.record = {
          assets: [],
          asset_ids: [],
          custom_fields: {},
        };
      }
      else {
        this.record = {};
      }
    },
    customModelConditions(field_name) {
      if(this.page.model === 'punchlist') {
        if(field_name === 'date') {
          return false;
        }
      }
      else if(this.page.model === 'punchlistitem') {
        //DEFAULT FIELDS THAT NEVER CAN BE EDITED
        let always_disabled_fields = ['date_punched', 'punched_by', 'punchlist_id', 'technical_authority_approved', 'executor_id'];
        //FIELDS THAT CAN BE EDITED ONLY BY PUNCHLIST OWNER
        let punchlist_owner_fields = ['active', 'status', 'follow_up_by', 'follow_up_wo_number', 'technical_authority_by', 'technical_authority_required', 'rejected', 'reject_reason'];
        if (always_disabled_fields.includes(field_name) || (punchlist_owner_fields.includes(field_name) && this.employee_id !== this.punchlist_owner_id && !this.headOfBFM)) {
          return false;
        }
      }
      else if(this.page.model === 'phase') {
        if(field_name === 'shutdown_required') {
          return (this.record.type === 'Construction' || this.record.type === 'Commissioning');
        }
        else if(field_name === 'shutdown_required_text' || field_name === 'shutdown_required_start_date' || field_name === 'shutdown_required_complete_date') {
          return ((this.record.type === 'Construction' || this.record.type === 'Commissioning') && this.record.shutdown_required === 'Yes');
        }
      }
      else if(this.page.model === 'asset') {
        if(field_name === 'name' && this.record.parent_id) {
          return false;
        }
      }
      else if(this.page.model === 'fieldset') {
        if(field_name === 'options_source') {
          return this.record.type === 'enum';
        }
        else if(field_name === 'options' || field_name === 'alternates') {
          return false;
        }
      }
      //DEFAULT
      return true;
    },
    createNewModel(field_name) {
      let data = { modal_name: this.modal_name, field_name: field_name };
      this.$emit('createNewModel', data);
    },
    canCreateNewModel(field_name) {
      return this.modal_name !== 'create_new_record' && this.page.model === 'article' &&
              ((field_name === 'tag_number_id' && this.can("create", this.$createEntity('tagnumber', { id: 0 }))) ||
              (field_name === 'brand_id' && this.can("create", this.$createEntity('brand', { id: 0 }))) ||
              (field_name === 'application_id' && this.can("create", this.$createEntity('application', { id: 0 }))) ||
              (field_name === 'markering_id' && this.can("create", this.$createEntity('markering', { id: 0 }))) ||
              (field_name === 'ip_type_id' && this.can("create", this.$createEntity('iptype', { id: 0 }))) ||
              (field_name === 'equipment_type_id' && this.can("create", this.$createEntity('equipmenttype', { id: 0 }))) ||
              (field_name === 'inspection_certificate_id' && this.can("create", this.$createEntity('inspectioncertificate', { id: 0 }))) ||
              (field_name === 'line_voltage_id' && this.can("create", this.$createEntity('linevoltage', { id: 0 }))));
    },
    usualFieldDataChanged(data) {
      if(this.record[data[0]] !== data[1]) {
        this.record[data[0]] = data[1];
        if (data[0] === 'project_id') {
          this.getProjectInfo();
        }
        else if (this.page.model === 'accesslevel' && data[0] === 'name') {
          if(data[1] === 'M0' || data[1] === 'IC0' || data[1] === 'V0' || data[1] === 'V1' || data[1] === 'V2' || data[1] === 'E0' || data[1] === 'IVB') {
            this.record.level = 1;
          }
          else if(data[1] === 'M1' || data[1] === 'IC1' || data[1] === 'IC2' || data[1] === 'E1') {
            this.record.level = 2;
          }
          else if(data[1] === 'M2' || data[1] === 'M3' || data[1] === 'IC3' || data[1] === 'E2') {
            this.record.level = 3;
          }
          else if(data[1] === 'M4' || data[1] === 'IC4') {
            this.record.level = 4;
          }
          else if(data[1] === 'M5' || data[1] === 'IC5' || data[1] === 'A1' || data[1] === 'E3') {
            this.record.level = 5;
          }
          else if(data[1] === 'IC6' || data[1] === 'A0') {
            this.record.level = 6;
          }
          else if(data[1] === 'A2') {
            this.record.level = 7;
          }
        }
        else if (this.page.model === 'article' && data[0] === 'root_asset_id') {
          this.root_asset_id = this.record.root_asset_id;
        }
        else if (this.page.model === 'project' && data[0] === 'root_asset_id') {
          this.getPlatformManagementInfo();
        }
        if (this.page.model === 'application' && (data[0] === 'equipment_group_id' || data[0] === 'equipment_category_id' || data[0] === 'explosive_atmosphere_id')) {
          if (data[0] === 'equipment_group_id') {
            if (data[2]) {
              this.application_equipment_group = data[2].split(" | ")[0]; //data[2] stores selected text value
            }
            else {
              this.application_equipment_group = null;
            }
          }
          else if (data[0] === 'equipment_category_id') {
            if (data[2]) {
              this.application_equipment_category = data[2].split(" | ")[0]; //data[2] stores selected text value
            }
            else {
              this.application_equipment_category = null;
            }
          }
          else if (data[0] === 'explosive_atmosphere_id') {
            if (data[2]) {
              this.application_explosive_atmosphere = data[2].split(" | ")[0]; //data[2] stores selected text value
            }
            else {
              this.application_explosive_atmosphere = null;
            }
          }
          this.record.name = "";
          if (this.application_equipment_group) {
            this.record.name = this.application_equipment_group + " ";
          }
          if (this.application_equipment_category) {
            this.record.name += this.application_equipment_category + " ";
          }
          if (this.application_explosive_atmosphere) {
            this.record.name += this.application_explosive_atmosphere;
          }
        }
        else if (this.page.model === 'markering' && (data[0] === 'regulation_id' || data[0] === 'protection_method_id' || data[0] === 'gas_group_id' || data[0] === 'temperature_class_id' || data[0] === 'equipment_protection_level_id')) {
          if (data[0] === 'regulation_id') {
            if (data[2]) {
              this.regulation = data[2].split(" | ")[0]; //data[2] stores selected text value
            } else {
              this.regulation = null;
            }
          } else if (data[0] === 'protection_method_id') {
            if (data[2]) {
              this.protection_method = data[2].split(" | ")[0]; //data[2] stores selected text value
            } else {
              this.protection_method = null;
            }
          } else if (data[0] === 'gas_group_id') {
            if (data[2]) {
              this.gas_group = data[2].split(" | ")[0]; //data[2] stores selected text value
            } else {
              this.gas_group = null;
            }
          } else if (data[0] === 'temperature_class_id') {
            if (data[2]) {
              this.temperature_class = data[2].split(" | ")[0]; //data[2] stores selected text value
              this.record.maximum_surface_temperature = data[2];
            } else {
              this.temperature_class = null;
              this.record.maximum_surface_temperature = '';
            }
          } else if (data[0] === 'equipment_protection_level_id') {
            if (data[2]) {
              this.equipment_protection_level = data[2].split(" | ")[0]; //data[2] stores selected text value
            } else {
              this.equipment_protection_level = null;
            }
          }
          this.record.name = "";
          if (this.regulation) {
            this.record.name = this.regulation + " ";
          }
          if (this.protection_method) {
            this.record.name += this.protection_method + " ";
          }
          if (this.gas_group) {
            this.record.name += this.gas_group + " ";
          }
          if (this.temperature_class) {
            this.record.name += this.temperature_class + " ";
          }
          if (this.equipment_protection_level) {
            this.record.name += this.equipment_protection_level;
          }
        }
      }
    },
    //PROJECT CUSTOM FUNCTION
    projectManagementFieldDataChanged(data) {
      this.record.project_management[data[0]] = data[1];
      if(data[0] === 'pc_type' && data[1] === 'PC-1' && this.platform_management) {
        this.record.project_management.tw_wbs_code = this.platform_management.tw_wbs_code;
      }
    },
    customFieldDataChanged(data) {
      this.record.custom_fields[data[0]] = data[1];
    },
    addressesChanged(updated_addresses) {
      this.record.addresses = updated_addresses;
    },
    tagsChanged(data) {
      this.record.tags = data;
    },
    async assetsAdded(ids) {
      this.record.asset_ids = ids;
      this.record.assets = [];
      for (let i = 0; i < this.record.asset_ids.length; i++) {
        let response = await helpFunctions.getOneFullAdditionalModel(this.$toast, "asset", this.record.asset_ids[i], false);
        this.record.assets.push(response);
      }
    },
    addDropFile(e) {
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        this.new_files.push(e.dataTransfer.files[i]);
      }
    },
    async uploadFiles(files, record_id) {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("attachment[]", files[i]);
      }
      formData.append("id", record_id);
      await helpFunctions.saveFile(this.$toast, this.page.name, formData);
    },
    async uploadOneFile(file, record_id) {
      const formData = new FormData();
      formData.append("attachment", file);
      formData.append("id", record_id);
      await helpFunctions.saveFile(this.$toast, this.page.name, formData);
    },
    //PUNCHLIST AND PROJECT MANAGEMENT CUSTOM FUNCTION
    async getProjectInfo() {
      if(this.record.project_id) {
        let response = await helpFunctions.getOneFullAdditionalModel(this.$toast, "project", this.record.project_id, true);
        this.record.name = response.name;
        this.record.tags = response.tags;
        if (this.page.model === 'punchlist') {
          this.record.location = response.root_asset_name;
          this.record.requisition = response.afe_pef;
          this.record.afe_pef = response.afe_pef;
        }
        else if (this.page.model === 'dnvverificationregister') {
          this.record.location = response.root_asset_name;
          this.record.wo_title = response.name;
          this.record.afe_pef = response.afe_pef;
          this.record.project_engineer_id = response.project_engineer_id;
        }
        else if (this.page.model === 'actionanddecision') {
          this.record.project_engineer_id = response.project_engineer_id;
        }
      }
      else {
        this.record.name = null;
        this.record.tags = [];
        if (this.page.model === 'punchlist') {
          this.record.location = null;
          this.record.requisition = null;
          this.record.afe_pef = null;
        }
        else if (this.page.model === 'dnvverificationregister') {
          this.record.location = null;
          this.record.wo_title = null;
          this.record.afe_pef = null;
          this.record.project_engineer_id = null;
        }
        else if (this.page.model === 'actionanddecision') {
          this.record.project_engineer_id = null;
        }
      }
    },
    async getPlatformManagementInfo() {
      if(this.record.root_asset_id) {
        this.platform_management = await helpFunctions.postCustomRoute(this.$toast, "get-platform-management-by-asset-id", { id: this.record.root_asset_id }, false, true);
        if (this.record.project_management.pc_type === 'PC-1') {
          this.record.project_management.tw_wbs_code = this.platform_management.tw_wbs_code;
        }
      }
    },
    async getFields() {
      const response = await helpFunctions.getFields(this.page.model, "show_on_creation");
      this.tags_required = response.tags_required;
      this.usual_fields = response.usual_fields;
      this.custom_fields = response.custom_fields;
    },
    //PROJECT CUSTOM FUNCTION
    async getProjectManagementFields() {
      let response = await helpFunctions.getFields("projectmanagement", "show_on_creation");
      this.project_management_fields = response.usual_fields;
    },
    //ASSET CUSTOM FUNCTION
    async loadChildrenAssets() {
      this.children_assets = await helpFunctions.postCustomRoute(this.$toast, "assets/get-children", { parent_id: this.record.parent_id }, false, true);
    },
    updateDescription() {
      let found_asset = this.children_assets.find((children_asset) => children_asset.name === this.record.name);
      if(found_asset) {
        this.record.id = found_asset.id;
        this.record.description = found_asset.description;
      }
      else {
        this.record.id = null;
        this.record.description = "";
      }
    },
  },
  computed: {
    headOfBFM() {
      return this.can("show", this.$createEntity("meeting", { id: 0 }));
    },
  },
};
</script>